import { createContext, useContext, useEffect, useState } from 'react';
import { MAPS_CONFIG } from '@/config/maps';
import { FallbackMap } from './fallback-map';

interface MapContextType {
  isLoaded: boolean;
  error: Error | null;
}

const MapContext = createContext<MapContextType>({
  isLoaded: false,
  error: null,
});

export function useMap() {
  return useContext(MapContext);
}

// Global script loading state
let isScriptLoaded = false;
let scriptLoadPromise: Promise<void> | null = null;

function loadMapScript(): Promise<void> {
  if (!MAPS_CONFIG.apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  if (window.google?.maps) {
    isScriptLoaded = true;
    return Promise.resolve();
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_CONFIG.apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      isScriptLoaded = true;
      resolve();
    };

    script.onerror = () => {
      document.getElementById('google-maps-script')?.remove();
      scriptLoadPromise = null;
      reject(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

interface MapProviderProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function MapProvider({ children, fallback = <FallbackMap /> }: MapProviderProps) {
  const [isLoaded, setIsLoaded] = useState(isScriptLoaded);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!MAPS_CONFIG.apiKey) {
      setError(new Error('Google Maps API key is not configured'));
      return;
    }

    if (isScriptLoaded) {
      setIsLoaded(true);
      return;
    }

    loadMapScript()
      .then(() => setIsLoaded(true))
      .catch((err) => setError(err));
  }, []);

  return (
    <MapContext.Provider value={{ isLoaded, error }}>
      {children}
    </MapContext.Provider>
  );
}